/**
 * Fretboard layout + keyboard-navigation math — pure, no React, no DOM.
 *
 * The <Fretboard> component is a thin SVG renderer over these helpers. Keeping
 * the geometry and the roving-focus rules here lets them be unit-tested without
 * a DOM.
 *
 * Coordinate convention: normalized [0,1]. `x` runs along the neck (nut at 0),
 * `y` runs across the strings with string 0 (low E) at the BOTTOM (y = 1),
 * matching how a right-handed player looks down at the neck.
 */

import type { Tuning, FretPosition } from "@music-app/theory-engine";

export interface FretWindow {
  /** Lowest fret shown (0 = open/nut). */
  from: number;
  /** Highest fret shown. */
  to: number;
}

export type FretNavKey =
  | "ArrowLeft"
  | "ArrowRight"
  | "ArrowUp"
  | "ArrowDown"
  | "Home"
  | "End";

/** Number of strings in a tuning. */
export function stringCount(tuning: Tuning): number {
  return tuning.openStrings.length;
}

/** The default full-neck window for a tuning. */
export function fullWindow(tuning: Tuning): FretWindow {
  return { from: 0, to: tuning.fretCount };
}

/** Frets included in a window, low to high. */
export function fretsInWindow(window: FretWindow): number[] {
  const out: number[] = [];
  for (let f = window.from; f <= window.to; f++) out.push(f);
  return out;
}

/**
 * Width of the open-string gutter, in fret-column units. When the window
 * includes the open position (fret 0), the nut is drawn this far in from the
 * left edge and open-string dots sit in the gutter to its left — matching a
 * real neck, where the open note sounds at the nut rather than in a fret space.
 */
const OPEN_GUTTER = 0.62;

interface XLayout {
  hasOpen: boolean;
  firstFretted: number;
  /** Normalized width of one fret column. */
  colW: number;
  /** Normalized x of the nut line. */
  nutX: number;
}

function xLayout(window: FretWindow): XLayout {
  const hasOpen = window.from === 0;
  const firstFretted = hasOpen ? 1 : window.from;
  const nFretted = Math.max(0, window.to - firstFretted + 1);
  const gutter = hasOpen ? OPEN_GUTTER : 0;
  const units = gutter + nFretted;
  const colW = units > 0 ? 1 / units : 1;
  return { hasOpen, firstFretted, colW, nutX: gutter * colW };
}

/** Normalized x of a fret's note centre. Open (fret 0) sits centred in the
 *  gutter left of the nut; fretted notes sit centred in the space just behind
 *  their fret wire (between wire f-1 and wire f), as on a real neck. */
export function fretCenterX(fret: number, window: FretWindow): number {
  const L = xLayout(window);
  if (L.hasOpen && fret === 0) return L.nutX / 2;
  return L.nutX + (fret - L.firstFretted + 0.5) * L.colW;
}

/** Normalized width of one fret column (for sizing dots / hit cells). */
export function fretColumnWidth(window: FretWindow): number {
  return xLayout(window).colW;
}

/** Normalized x of the nut line. */
export function nutX(window: FretWindow): number {
  return xLayout(window).nutX;
}

/** Normalized x of every fret wire (the wire at the bridge edge of each fretted
 *  column), low fret to high. Excludes the nut — draw that separately. */
export function fretWireXs(window: FretWindow): number[] {
  const L = xLayout(window);
  const out: number[] = [];
  for (let f = L.firstFretted; f <= window.to; f++) {
    out.push(L.nutX + (f - L.firstFretted + 1) * L.colW);
  }
  return out;
}

/**
 * Normalized centre of a fret cell. Open (fret 0) sits in the gutter left of
 * the nut; fretted notes sit centred behind their fret wire. `y` puts string 0
 * at the bottom.
 */
export function cellCenter(
  tuning: Tuning,
  pos: FretPosition,
  window: FretWindow,
): { x: number; y: number } {
  const x = fretCenterX(pos.fret, window);
  const strings = stringCount(tuning);
  // string 0 → bottom (y near 1); highest string → top (y near 0).
  const y = strings <= 1 ? 0.5 : 1 - pos.stringIndex / (strings - 1);
  return { x, y };
}

/** Resolve the strings a user may interact with, sorted ascending. */
function resolveAllowed(tuning: Tuning, allowedStrings?: readonly number[]): number[] {
  const all = Array.from({ length: stringCount(tuning) }, (_, i) => i);
  if (!allowedStrings) return all;
  const set = new Set(allowedStrings);
  return all.filter((s) => set.has(s));
}

/**
 * Where focus moves on an arrow / Home / End key, under the roving-tabindex
 * grid model. Left/Right step the fret within the window; Up/Down step to the
 * next interactive string (Up = higher-pitched = higher stringIndex, which is
 * visually up); Home/End jump to the window ends on the current string. The
 * result is clamped — at an edge, focus stays put.
 */
export function nextFocus(
  current: FretPosition,
  key: FretNavKey,
  opts: { tuning: Tuning; window: FretWindow; allowedStrings?: readonly number[] },
): FretPosition {
  const { tuning, window, allowedStrings } = opts;
  switch (key) {
    case "ArrowLeft":
      return { ...current, fret: Math.max(window.from, current.fret - 1) };
    case "ArrowRight":
      return { ...current, fret: Math.min(window.to, current.fret + 1) };
    case "Home":
      return { ...current, fret: window.from };
    case "End":
      return { ...current, fret: window.to };
    case "ArrowUp":
    case "ArrowDown": {
      const allowed = resolveAllowed(tuning, allowedStrings);
      const candidates =
        key === "ArrowUp"
          ? allowed.filter((s) => s > current.stringIndex)
          : allowed.filter((s) => s < current.stringIndex);
      if (candidates.length === 0) return current;
      const nextString =
        key === "ArrowUp" ? Math.min(...candidates) : Math.max(...candidates);
      return { ...current, stringIndex: nextString };
    }
  }
}

/** A sensible initial focus: the lowest interactive string at the window start. */
export function initialFocus(
  tuning: Tuning,
  window: FretWindow,
  allowedStrings?: readonly number[],
): FretPosition {
  const allowed = resolveAllowed(tuning, allowedStrings);
  return { stringIndex: allowed[0] ?? 0, fret: window.from };
}
